import { useTranslation } from "react-i18next";
import { FAB, ProgressBar, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import { LessonCardShell } from "./LessonCardShell";
import { LessonMetaRow } from "./LessonMetaRow";
import type { ContinueLearningLesson } from "./types";

type Props = {
	lesson: ContinueLearningLesson;
	onResumePress: (lesson: ContinueLearningLesson) => void;
	cardWidth?: number;
};

export const ContinueLearningCard = ({
	lesson,
	onResumePress,
	cardWidth = 320,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const progressPercent = Math.min(Math.max(lesson.progressPercent, 0), 100);
	const progress = progressPercent / 100;
	const hasThumbnail = Boolean(lesson.thumbnailUrl);
	const ctaLabel = lesson.ctaLabel ?? t("lessons.home.card.resumeCta");

	return (
		<LessonCardShell
			cardWidth={cardWidth}
			thumbnailUrl={lesson.thumbnailUrl}
			onPress={() => onResumePress(lesson)}
			coverOverlay={
				<>
					<Box
						position="absolute"
						left={0}
						right={0}
						top={0}
						bottom={0}
						backgroundColor={theme.colors.scrim}
						style={{ opacity: 0.18 }}
					/>
					<Box
						position="absolute"
						left={0}
						right={0}
						justifyContent="center"
						alignItems="center"
					>
						<FAB
							label={ctaLabel}
							icon={({ color }) => (
								<AppIcon name="play" size={24} color={color} />
							)}
							style={{
								borderRadius: theme.roundness * 100,
							}}
							onPress={() => onResumePress(lesson)}
						/>
					</Box>
				</>
			}
		>
			<Box gap={20}>
				<Box gap={8}>
					<Text variant="titleMedium" numberOfLines={2}>
						{lesson.title}
					</Text>
					<LessonMetaRow meta={lesson.meta} />
				</Box>
				<Box gap={8}>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						gap={12}
					>
						<Text variant="labelMedium">
							{t("lessons.home.card.progress", {
								value: progressPercent,
							})}
						</Text>
						<Text variant="labelMedium">
							{t("lessons.home.card.timeLeft", {
								count: lesson.timeRemainingMinutes,
							})}
						</Text>
					</Box>
					<ProgressBar
						progress={progress}
						color={theme.colors.primary}
						style={{
							width: "100%",
							backgroundColor: theme.colors.outlineVariant,
							borderRadius: theme.roundness * 100,
						}}
					/>
				</Box>
				{!hasThumbnail && (
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						gap={8}
					>
						<Text variant="bodyMedium" style={{ color: theme.colors.primary }}>
							{ctaLabel}
						</Text>
						<AppIcon
							size={20}
							color={theme.colors.primary}
							name="play-circle-outline"
						/>
					</Box>
				)}
			</Box>
		</LessonCardShell>
	);
};
